import { useEffect, useRef, useState, useMemo } from "react";
import { useParams } from "react-router-dom";
import { estimateExtractionApi } from "@services/api/estimateExtraction";
import type { ClientDevisData, EstimateExtractionItem } from "types/estimateExtraction";

function formatPrice(v: number | null | undefined): string {
  if (v == null) return "-";
  return v.toLocaleString("fr-FR", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + " \u20AC";
}

function getItemNet(item: EstimateExtractionItem): number {
  return item.total_after_remise ?? item.total ?? 0;
}

function formatDeadline(isoDate: string): string {
  try {
    const d = new Date(isoDate);
    const day = d.toLocaleDateString("fr-FR", { day: "numeric", month: "long" });
    const time = d.toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" });
    return `${day} à ${time}`;
  } catch {
    return "";
  }
}

type ScreenState = "loading" | "error" | "ready" | "preview" | "otp_verification" | "signing" | "submitted" | "modify_select";

export default function DevisScreen() {
  const { token } = useParams<{ token: string }>();
  const [state, setState] = useState<ScreenState>("loading");
  const [data, setData] = useState<ClientDevisData | null>(null);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [submitting, setSubmitting] = useState(false);
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());
  const [pdfUrl, setPdfUrl] = useState<string>("");
  const [ordreId, setOrdreId] = useState<string>("");
  const [isModification, setIsModification] = useState(false);

  // Signature state
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const isDrawingRef = useRef(false);
  const [hasSigned, setHasSigned] = useState(false);
  const [signingSubmitting, setSigningSubmitting] = useState(false);

  // OTP state
  const [otpCode, setOtpCode] = useState("");
  const [otpSending, setOtpSending] = useState(false);
  const [otpVerifying, setOtpVerifying] = useState(false);
  const [otpSent, setOtpSent] = useState(false);
  const [otpError, setOtpError] = useState("");
  const [otpSessionToken, setOtpSessionToken] = useState("");

  useEffect(() => {
    if (!token) { setState("error"); return; }
    estimateExtractionApi.getClientDevis(token)
      .then((d) => {
        setData(d);
        if (d.status === "accepted") {
          if (d.pdf_url) setPdfUrl(d.pdf_url);
          if (d.ordre_id) setOrdreId(d.ordre_id);
          if (d.can_modify && d.client_selection) {
            setSelectedIds(new Set(d.client_selection));
          }
          setState("submitted");
        } else if (d.status === "superseded") {
          setState("error");
        } else if (d.status === "expired") {
          setState("error");
        } else if (d.status === "selection_done" && d.pdf_url) {
          // Client already submitted selection, show PDF preview
          setPdfUrl(d.pdf_url);
          if (d.client_selection) {
            setSelectedIds(new Set(d.client_selection));
          }
          setState("preview");
        } else {
          // Pre-select all items, respecting previous client selection
          if (d.client_selection) {
            setSelectedIds(new Set(d.client_selection));
          } else {
            setSelectedIds(new Set(d.items.map((i) => i.id)));
          }
          setState("ready");
        }
      })
      .catch(() => setState("error"));
  }, [token]);

  // Canvas drawing logic
  useEffect(() => {
    if (state !== "signing") return;
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    // Set canvas internal resolution to match CSS display size × devicePixelRatio
    const cssRect = canvas.getBoundingClientRect();
    const dpr = window.devicePixelRatio || 1;
    canvas.width = cssRect.width * dpr;
    canvas.height = cssRect.height * dpr;

    // Map CSS touch/mouse coordinates to canvas pixel coordinates
    const getPos = (clientX: number, clientY: number) => {
      const r = canvas.getBoundingClientRect();
      return {
        x: (clientX - r.left) * (canvas.width / r.width),
        y: (clientY - r.top) * (canvas.height / r.height),
      };
    };

    const startDraw = (x: number, y: number) => {
      isDrawingRef.current = true;
      ctx.beginPath();
      ctx.moveTo(x, y);
    };
    const draw = (x: number, y: number) => {
      if (!isDrawingRef.current) return;
      ctx.lineTo(x, y);
      ctx.strokeStyle = "#1e293b";
      ctx.lineWidth = 2 * dpr;
      ctx.lineCap = "round";
      ctx.lineJoin = "round";
      ctx.stroke();
      setHasSigned(true);
    };
    const stopDraw = () => { isDrawingRef.current = false; };

    const onMouseDown = (e: MouseEvent) => {
      const { x, y } = getPos(e.clientX, e.clientY);
      startDraw(x, y);
    };
    const onMouseMove = (e: MouseEvent) => {
      const { x, y } = getPos(e.clientX, e.clientY);
      draw(x, y);
    };
    const onTouchStart = (e: TouchEvent) => {
      e.preventDefault();
      const t = e.touches[0];
      const { x, y } = getPos(t.clientX, t.clientY);
      startDraw(x, y);
    };
    const onTouchMove = (e: TouchEvent) => {
      e.preventDefault();
      const t = e.touches[0];
      const { x, y } = getPos(t.clientX, t.clientY);
      draw(x, y);
    };

    canvas.addEventListener("mousedown", onMouseDown);
    canvas.addEventListener("mousemove", onMouseMove);
    canvas.addEventListener("mouseup", stopDraw);
    canvas.addEventListener("mouseleave", stopDraw);
    canvas.addEventListener("touchstart", onTouchStart, { passive: false });
    canvas.addEventListener("touchmove", onTouchMove, { passive: false });
    canvas.addEventListener("touchend", stopDraw);

    return () => {
      canvas.removeEventListener("mousedown", onMouseDown);
      canvas.removeEventListener("mousemove", onMouseMove);
      canvas.removeEventListener("mouseup", stopDraw);
      canvas.removeEventListener("mouseleave", stopDraw);
      canvas.removeEventListener("touchstart", onTouchStart);
      canvas.removeEventListener("touchmove", onTouchMove);
      canvas.removeEventListener("touchend", stopDraw);
    };
  }, [state]);

  const clearCanvas = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    ctx?.clearRect(0, 0, canvas.width, canvas.height);
    setHasSigned(false);
  };

  const toggleItem = (item: EstimateExtractionItem) => {
    if (item.obligatoire) return;
    if (data?.locked_items?.includes(item.id)) return;
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(item.id)) next.delete(item.id);
      else next.add(item.id);
      return next;
    });
  };

  const toggleExpand = (id: string) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const { totalHT, totalTVA, totalTTC } = useMemo(() => {
    if (!data) return { totalHT: 0, totalTVA: 0, totalTTC: 0 };
    const rate = data.tva_rate || 20;
    let htNet = 0;
    let tva = 0;
    for (const item of data.items) {
      if (selectedIds.has(item.id)) {
        htNet += getItemNet(item);
        let itemTva = 0;
        if (item.forfait_ttc != null) {
          // Forfait: fixed package TTC from ServiceBox; derive VAT from the package HT.
          itemTva = Math.round((item.forfait_ttc - getItemNet(item)) * 100) / 100;
        } else if (item.sub_items.length > 0) {
          for (const sub of item.sub_items) {
            const subRemise = sub.remise ?? 0;
            if (sub.unit_price != null && sub.quantity != null) {
              let subTva = Math.round(sub.unit_price * rate) / 100 * sub.quantity;
              if (subRemise > 0) {
                subTva = Math.round(subTva * (1 - subRemise / 100) * 100) / 100;
              }
              itemTva += subTva;
            } else if (sub.total != null) {
              itemTva += Math.round(sub.total * rate) / 100;
            }
          }
        } else {
          itemTva = Math.round(getItemNet(item) * rate) / 100;
        }
        // Fallback: item-level remise (legacy extractions) — forfait postes carry no remise
        if (item.forfait_ttc == null && item.remise != null && item.remise > 0) {
          itemTva = Math.round(itemTva * (1 - item.remise / 100) * 100) / 100;
        }
        tva += itemTva;
      }
    }
    tva = Math.round(tva * 100) / 100;
    return { totalHT: htNet, totalTVA: tva, totalTTC: Math.round((htNet + tva) * 100) / 100 };
  }, [data, selectedIds]);

  const hasNewSelection = useMemo(() => {
    if (!data?.locked_items) return true;
    const lockedSet = new Set(data.locked_items);
    return Array.from(selectedIds).some(
      (id) => !lockedSet.has(id) && !data.items.find((i) => i.id === id && i.obligatoire)
    );
  }, [data, selectedIds]);

  const handleSubmit = async () => {
    if (!token) return;
    setSubmitting(true);
    try {
      const result = await estimateExtractionApi.submitClientSelection(token, Array.from(selectedIds));
      setPdfUrl(result.pdf_url);
      setState("preview");
    } catch {
      alert("Erreur lors de la validation");
    } finally {
      setSubmitting(false);
    }
  };

  const handleBackToSelection = () => {
    setState(isModification ? "modify_select" : "ready");
  };

  const handleGoToSign = () => {
    if (data?.requires_otp) {
      setOtpCode("");
      setOtpError("");
      setOtpSent(false);
      setState("otp_verification");
    } else {
      setHasSigned(false);
      setState("signing");
    }
  };

  const handleStartModification = () => {
    if (!data) return;
    setIsModification(true);
    if (data.client_selection) {
      setSelectedIds(new Set(data.client_selection));
    }
    setState("modify_select");
  };

  const handleSendOtp = async () => {
    if (!token) return;
    setOtpSending(true);
    setOtpError("");
    try {
      const result = await estimateExtractionApi.sendDevisOtp(token);
      if (result.success) {
        setOtpSent(true);
      } else {
        setOtpError(result.message === "sms_failed" ? "Erreur d'envoi du SMS. Veuillez réessayer." : result.message);
      }
    } catch {
      setOtpError("Erreur lors de l'envoi du code.");
    } finally {
      setOtpSending(false);
    }
  };

  const handleVerifyOtp = async () => {
    if (!token || !otpCode.trim()) return;
    setOtpVerifying(true);
    setOtpError("");
    try {
      const result = await estimateExtractionApi.verifyDevisOtp(token, otpCode.trim());
      if (result.success) {
        if (result.session_token) {
          setOtpSessionToken(result.session_token);
        }
        setHasSigned(false);
        setState("signing");
      } else {
        if (result.message === "otp_expired") {
          setOtpError("Le code a expiré. Veuillez en demander un nouveau.");
        } else if (result.message === "max_attempts") {
          setOtpError("Nombre maximum de tentatives atteint. Veuillez demander un nouveau code.");
        } else if (result.message.startsWith("invalid_code:")) {
          const remaining = result.message.split(":")[1];
          setOtpError(`Code incorrect. ${remaining} tentative(s) restante(s).`);
        } else {
          setOtpError("Code incorrect.");
        }
      }
    } catch {
      setOtpError("Erreur lors de la vérification.");
    } finally {
      setOtpVerifying(false);
    }
  };

  const handleSign = async () => {
    if (!token || !canvasRef.current) return;
    setSigningSubmitting(true);
    try {
      const dataUrl = canvasRef.current.toDataURL("image/png");
      const blob = await (await fetch(dataUrl)).blob();
      const file = new File([blob], "signature.png", { type: "image/png" });
      const result = await estimateExtractionApi.signDevis(token, file, otpSessionToken || undefined);
      if (result.pdf_url) setPdfUrl(result.pdf_url);
      if (result.ordre_id) setOrdreId(result.ordre_id);
      const refreshed = await estimateExtractionApi.getClientDevis(token);
      setData(refreshed);
      setIsModification(false);
      setState("submitted");
    } catch {
      alert("Erreur lors de la signature");
    } finally {
      setSigningSubmitting(false);
    }
  };

  if (state === "loading") {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="w-6 h-6 border-2 border-[#5C6BFF] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (state === "error") {
    return (
      <div className="min-h-screen bg-white flex flex-col items-center justify-center gap-4 p-5">
        <div style={{ fontSize: 40 }}>&#128270;</div>
        <p style={{ fontSize: 13, fontWeight: 500, color: "#A1A1A1", textAlign: "center" }}>
          {data?.status === "superseded"
            ? "Ce devis a été remplacé par une version plus récente. Consultez vos emails pour le nouveau lien."
            : data?.status === "expired" ? "Ce lien a expiré." : "Lien invalide."}
        </p>
      </div>
    );
  }

  if (state === "submitted") {
    const canModify = data?.can_modify === true;
    const deadlineStr = data?.modification_deadline ? formatDeadline(data.modification_deadline) : "";

    return (
      <div className="min-h-screen bg-[#FAFAFA] flex flex-col items-center">
        <div className="w-full max-w-[600px] bg-white min-h-screen flex flex-col">
          {/* Success header */}
          <div style={{ padding: "24px 20px", borderBottom: "1px solid #E5E5E5", textAlign: "center" }}>
            <div style={{ width: 48, height: 48, borderRadius: "50%", background: "#D1FAE5", display: "inline-flex", alignItems: "center", justifyContent: "center", marginBottom: 12 }}>
              <svg width="24" height="24" viewBox="0 0 24 24" fill="#065F46">
                <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41L9 16.17z" />
              </svg>
            </div>
            <p style={{ fontSize: 18, fontWeight: 600, margin: 0 }}>
              {isModification ? "Devis mis à jour" : "Devis signé"}
            </p>
            <p style={{ fontSize: 13, color: "#A1A1A1", marginTop: 4 }}>
              {isModification
                ? "Votre devis a été mis à jour avec les options supplémentaires."
                : "Merci, votre devis a été signé avec succès."}
            </p>
          </div>

          {/* Modification window info */}
          {canModify && deadlineStr && (
            <div style={{ margin: "16px 16px 0", padding: "12px 16px", background: "#EFF6FF", borderRadius: 8, border: "1px solid #BFDBFE" }}>
              <p style={{ fontSize: 13, color: "#1E40AF", margin: 0, fontWeight: 500 }}>
                Vous pouvez ajouter des options supplémentaires jusqu'au {deadlineStr} en revisitant ce même lien.
              </p>
            </div>
          )}

          {/* Signed PDF preview */}
          {pdfUrl && (
            <div style={{ flex: 1, padding: 16 }}>
              <iframe
                src={pdfUrl}
                style={{ width: "100%", height: canModify ? "calc(100vh - 360px)" : "calc(100vh - 260px)", border: "1px solid #E5E5E5", borderRadius: 8 }}
                title="Devis signé"
              />
            </div>
          )}

          {/* Bottom buttons */}
          <div style={{
            position: "sticky",
            bottom: 0,
            background: "white",
            borderTop: "1px solid #E5E5E5",
            padding: 16,
            display: "flex",
            flexDirection: "column",
            gap: 10,
          }}>
            {canModify && (
              <button
                onClick={handleStartModification}
                style={{
                  width: "100%",
                  padding: 15,
                  background: "white",
                  color: "#5C6BFF",
                  fontWeight: 700,
                  fontSize: 15,
                  borderRadius: 10,
                  border: "1.5px solid #5C6BFF",
                  cursor: "pointer",
                }}
              >
                Ajouter des options
              </button>
            )}
            <button
              onClick={() => {
                if (ordreId) {
                  window.location.href = `/tracking/${ordreId}`;
                } else {
                  window.close();
                }
              }}
              style={{
                width: "100%",
                padding: 15,
                background: "#111",
                color: "white",
                fontWeight: 700,
                fontSize: 15,
                borderRadius: 10,
                border: "none",
                cursor: "pointer",
              }}
            >
              {ordreId ? "Suivre mon ordre" : "Fermer"}
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (state === "modify_select") {
    if (!data) return null;

    const lockedSet = new Set(data.locked_items || []);

    return (
      <div className="min-h-screen bg-[#FAFAFA] flex flex-col items-center">
        <div className="w-full max-w-[600px] bg-white min-h-screen flex flex-col">
          {/* Header */}
          <div style={{ padding: "20px 20px 16px", borderBottom: "1px solid #E5E5E5" }}>
            <div style={{ fontSize: 11, fontWeight: 500, color: "#A1A1A1" }}>
              {data.org_name} {data.ordre_ref ? `\u2022 ${data.ordre_ref}` : ""}
            </div>
            <div style={{ fontSize: 18, fontWeight: 700, marginTop: 4 }}>Ajouter des options</div>
            <div style={{ fontSize: 12, color: "#6B7280", marginTop: 4 }}>
              Sélectionnez les options supplémentaires que vous souhaitez ajouter.
            </div>
          </div>

          {/* Items */}
          <div style={{ padding: 16, flex: 1 }}>
            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              {data.items.map((item) => {
                const selected = selectedIds.has(item.id);
                const isLocked = item.obligatoire || lockedSet.has(item.id);
                const expanded = expandedIds.has(item.id);

                return (
                  <div
                    key={item.id}
                    style={{
                      border: `1.5px solid ${selected ? "#5C6BFF" : "#E5E5E5"}`,
                      borderRadius: 10,
                      overflow: "hidden",
                      opacity: selected ? 1 : 0.5,
                      transition: "all 0.15s",
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 12,
                        padding: "12px 14px",
                        cursor: isLocked ? "default" : "pointer",
                        background: selected ? "#FAFBFF" : "#FAFAFA",
                      }}
                      onClick={() => toggleItem(item)}
                    >
                      {/* Checkbox */}
                      <div
                        style={{
                          width: 20,
                          height: 20,
                          borderRadius: 5,
                          border: `2px solid ${isLocked ? "#D1D5DB" : selected ? "#5C6BFF" : "#D1D5DB"}`,
                          background: selected ? (isLocked ? "#D1D5DB" : "#5C6BFF") : "white",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          flexShrink: 0,
                        }}
                      >
                        {selected && (
                          <svg width="12" height="12" viewBox="0 0 24 24" fill="white">
                            <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41L9 16.17z" />
                          </svg>
                        )}
                      </div>

                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: 13, fontWeight: 600, lineHeight: 1.3 }}>{item.libelle}</div>
                        {isLocked && (
                          <span style={{ fontSize: 10, color: "#9CA3AF", fontWeight: 500 }}>
                            {item.obligatoire ? "Inclus obligatoirement" : "Déjà sélectionné"}
                          </span>
                        )}
                      </div>

                      <div style={{ textAlign: "right", flexShrink: 0 }}>
                        <div style={{ fontSize: 14, fontWeight: 700 }}>{formatPrice(getItemNet(item))}</div>
                        {item.remise != null && item.remise > 0 && (
                          <div style={{ fontSize: 10, color: "#EF4444" }}>-{item.remise}%</div>
                        )}
                      </div>

                      <div style={{ width: 32, flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "center" }}>
                        {item.sub_items.length > 0 && (
                          <button
                            style={{ padding: 10, margin: -10, color: "#9CA3AF", borderRadius: 6 }}
                            onClick={(e) => { e.stopPropagation(); toggleExpand(item.id); }}
                          >
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" style={{ transform: expanded ? "rotate(180deg)" : undefined, transition: "0.15s" }}>
                              <path d="M7 10l5 5 5-5H7z" />
                            </svg>
                          </button>
                        )}
                      </div>
                    </div>

                    {expanded && item.sub_items.length > 0 && (
                      <div style={{ borderTop: "1px solid #F0F0F0", padding: "4px 0" }}>
                        {item.sub_items.map((sub, i) => (
                          <div
                            key={i}
                            style={{
                              display: "flex",
                              justifyContent: "space-between",
                              alignItems: "center",
                              padding: "4px 14px 4px 48px",
                              fontSize: 11,
                              color: "#6B7280",
                            }}
                          >
                            <span>
                              <span style={{
                                display: "inline-block",
                                fontSize: 9,
                                fontWeight: 600,
                                padding: "1px 4px",
                                borderRadius: 3,
                                marginRight: 6,
                                background: sub.type === "piece" ? "#EEF2FF" : "#FEF3C7",
                                color: sub.type === "piece" ? "#4338CA" : "#92400E",
                              }}>
                                {sub.type === "piece" ? "PR" : "MO"}
                              </span>
                              {sub.libelle}
                            </span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          {/* Bottom bar */}
          <div style={{
            position: "sticky",
            bottom: 0,
            background: "white",
            borderTop: "1px solid #E5E5E5",
            padding: 16,
          }}>
            <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 12, fontSize: 12 }}>
              <span style={{ color: "#6B7280" }}>Total HT</span>
              <span style={{ fontWeight: 600 }}>{formatPrice(totalHT)}</span>
            </div>
            <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 12, fontSize: 12 }}>
              <span style={{ color: "#6B7280" }}>TVA ({data.tva_rate || 20}%)</span>
              <span style={{ fontWeight: 600 }}>{formatPrice(totalTVA)}</span>
            </div>
            <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 16, fontSize: 15 }}>
              <span style={{ fontWeight: 700 }}>Total TTC</span>
              <span style={{ fontWeight: 700, color: "#5C6BFF" }}>{formatPrice(totalTTC)}</span>
            </div>
            <div style={{ display: "flex", gap: 12 }}>
              <button
                onClick={() => {
                  setIsModification(false);
                  setState("submitted");
                }}
                style={{
                  flex: 1,
                  padding: 15,
                  background: "white",
                  color: "#111",
                  fontWeight: 600,
                  fontSize: 14,
                  borderRadius: 10,
                  border: "1.5px solid #E5E5E5",
                  cursor: "pointer",
                }}
              >
                Annuler
              </button>
              <button
                onClick={handleSubmit}
                disabled={submitting || !hasNewSelection}
                style={{
                  flex: 1,
                  padding: 15,
                  background: hasNewSelection && !submitting ? "#111" : "#94a3b8",
                  color: "white",
                  fontWeight: 700,
                  fontSize: 15,
                  borderRadius: 10,
                  border: "none",
                  cursor: hasNewSelection && !submitting ? "pointer" : "not-allowed",
                  opacity: submitting ? 0.7 : 1,
                }}
              >
                {submitting ? "Validation..." : "Valider ma sélection"}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (state === "preview") {
    return (
      <div className="min-h-screen bg-[#FAFAFA] flex flex-col items-center">
        <div className="w-full max-w-[600px] bg-white min-h-screen flex flex-col">
          {/* Header */}
          <div style={{ padding: "20px 20px 16px", borderBottom: "1px solid #E5E5E5" }}>
            <div style={{ fontSize: 11, fontWeight: 500, color: "#A1A1A1" }}>
              {data?.org_name} {data?.ordre_ref ? `\u2022 ${data.ordre_ref}` : ""}
            </div>
            <div style={{ fontSize: 18, fontWeight: 700, marginTop: 4 }}>Votre devis</div>
            <div style={{ fontSize: 12, color: "#6B7280", marginTop: 4 }}>
              Vérifiez le document puis signez pour valider.
            </div>
          </div>

          {/* PDF Preview */}
          <div style={{ flex: 1, padding: 16 }}>
            <iframe
              src={pdfUrl}
              style={{ width: "100%", height: "calc(100vh - 220px)", border: "1px solid #E5E5E5", borderRadius: 8 }}
              title="Aperçu du devis"
            />
          </div>

          {/* Bottom buttons */}
          <div style={{
            position: "sticky",
            bottom: 0,
            background: "white",
            borderTop: "1px solid #E5E5E5",
            padding: 16,
            display: "flex",
            gap: 12,
          }}>
            <button
              onClick={handleBackToSelection}
              style={{
                flex: 1,
                padding: 15,
                background: "white",
                color: "#111",
                fontWeight: 600,
                fontSize: 14,
                borderRadius: 10,
                border: "1.5px solid #E5E5E5",
                cursor: "pointer",
              }}
            >
              Modifier ma sélection
            </button>
            <button
              onClick={handleGoToSign}
              style={{
                flex: 1,
                padding: 15,
                background: "#111",
                color: "white",
                fontWeight: 700,
                fontSize: 14,
                borderRadius: 10,
                border: "none",
                cursor: "pointer",
              }}
            >
              Signer le devis
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (state === "otp_verification") {
    return (
      <div className="min-h-screen bg-[#FAFAFA] flex flex-col items-center">
        <div className="w-full max-w-[600px] bg-white min-h-screen flex flex-col">
          {/* Header */}
          <div style={{ padding: "20px 20px 16px", borderBottom: "1px solid #E5E5E5" }}>
            <div style={{ fontSize: 11, fontWeight: 500, color: "#A1A1A1" }}>
              {data?.org_name} {data?.ordre_ref ? `\u2022 ${data.ordre_ref}` : ""}
            </div>
            <div style={{ fontSize: 18, fontWeight: 700, marginTop: 4 }}>Vérification d'identité</div>
            <div style={{ fontSize: 12, color: "#6B7280", marginTop: 4 }}>
              Pour sécuriser votre signature, un code de vérification va vous être envoyé
              par SMS au numéro {data?.signer_phone_masked || "enregistré"}.
            </div>
          </div>

          <div style={{ flex: 1, padding: 20, display: "flex", flexDirection: "column", gap: 16 }}>
            {!otpSent ? (
              <button
                onClick={handleSendOtp}
                disabled={otpSending}
                style={{
                  padding: 15,
                  background: "#111",
                  color: "white",
                  fontWeight: 700,
                  fontSize: 15,
                  borderRadius: 10,
                  border: "none",
                  cursor: otpSending ? "wait" : "pointer",
                  opacity: otpSending ? 0.7 : 1,
                }}
              >
                {otpSending ? "Envoi..." : "Recevoir le code par SMS"}
              </button>
            ) : (
              <>
                <div style={{ fontSize: 13, color: "#065F46", background: "#D1FAE5", padding: "10px 14px", borderRadius: 8 }}>
                  Code envoyé par SMS au numéro {data?.signer_phone_masked || "enregistré"}.
                </div>
                <div>
                  <label style={{ fontSize: 13, fontWeight: 600, display: "block", marginBottom: 6 }}>
                    Code de vérification
                  </label>
                  <input
                    type="text"
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    maxLength={6}
                    value={otpCode}
                    onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, ""))}
                    placeholder="000000"
                    style={{
                      width: "100%",
                      padding: "14px 16px",
                      fontSize: 20,
                      fontWeight: 700,
                      letterSpacing: 8,
                      textAlign: "center",
                      border: "1.5px solid #E5E5E5",
                      borderRadius: 10,
                      outline: "none",
                      boxSizing: "border-box",
                    }}
                  />
                </div>
                <button
                  onClick={handleVerifyOtp}
                  disabled={otpVerifying || otpCode.length < 6}
                  style={{
                    padding: 15,
                    background: otpCode.length >= 6 && !otpVerifying ? "#111" : "#94a3b8",
                    color: "white",
                    fontWeight: 700,
                    fontSize: 15,
                    borderRadius: 10,
                    border: "none",
                    cursor: otpCode.length >= 6 && !otpVerifying ? "pointer" : "not-allowed",
                    opacity: otpVerifying ? 0.7 : 1,
                  }}
                >
                  {otpVerifying ? "Vérification..." : "Vérifier le code"}
                </button>
                <button
                  onClick={handleSendOtp}
                  disabled={otpSending}
                  style={{
                    padding: 10,
                    background: "transparent",
                    color: "#6B7280",
                    fontWeight: 500,
                    fontSize: 13,
                    border: "none",
                    cursor: "pointer",
                    textDecoration: "underline",
                  }}
                >
                  Renvoyer le code
                </button>
              </>
            )}
            {otpError && (
              <div style={{ fontSize: 13, color: "#DC2626", background: "#FEF2F2", padding: "10px 14px", borderRadius: 8 }}>
                {otpError}
              </div>
            )}
          </div>

          {/* Bottom: back button */}
          <div style={{
            position: "sticky",
            bottom: 0,
            background: "white",
            borderTop: "1px solid #E5E5E5",
            padding: 16,
          }}>
            <button
              onClick={() => setState("preview")}
              style={{
                width: "100%",
                padding: 15,
                background: "white",
                color: "#111",
                fontWeight: 600,
                fontSize: 14,
                borderRadius: 10,
                border: "1.5px solid #E5E5E5",
                cursor: "pointer",
              }}
            >
              Retour
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (state === "signing") {
    return (
      <div className="min-h-screen bg-[#FAFAFA] flex flex-col items-center">
        <div className="w-full max-w-[600px] bg-white min-h-screen flex flex-col">
          {/* Header */}
          <div style={{ padding: "20px 20px 16px", borderBottom: "1px solid #E5E5E5" }}>
            <div style={{ fontSize: 11, fontWeight: 500, color: "#A1A1A1" }}>
              {data?.org_name} {data?.ordre_ref ? `\u2022 ${data.ordre_ref}` : ""}
            </div>
            <div style={{ fontSize: 18, fontWeight: 700, marginTop: 4 }}>Signer le devis</div>
            <div style={{ fontSize: 12, color: "#6B7280", marginTop: 4 }}>
              Signez dans le cadre ci-dessous pour valider votre devis.
            </div>
          </div>

          {/* Signature canvas */}
          <div style={{ flex: 1, padding: 16, display: "flex", flexDirection: "column", gap: 16 }}>
            <canvas
              ref={canvasRef}
              style={{
                border: "2px dashed #cbd5e1",
                borderRadius: 8,
                background: "#f8fafc",
                width: "100%",
                height: 200,
                touchAction: "none",
                cursor: signingSubmitting ? "not-allowed" : "crosshair",
              }}
            />
            <div style={{ display: "flex", justifyContent: "flex-end" }}>
              <button
                onClick={clearCanvas}
                disabled={signingSubmitting}
                style={{
                  padding: "8px 20px",
                  borderRadius: 8,
                  border: "1px solid #E5E5E5",
                  background: "white",
                  color: "#6B7280",
                  cursor: "pointer",
                  fontSize: 13,
                }}
              >
                Effacer
              </button>
            </div>
          </div>

          {/* Bottom buttons */}
          <div style={{
            position: "sticky",
            bottom: 0,
            background: "white",
            borderTop: "1px solid #E5E5E5",
            padding: 16,
            display: "flex",
            gap: 12,
          }}>
            <button
              onClick={() => setState("preview")}
              disabled={signingSubmitting}
              style={{
                flex: 1,
                padding: 15,
                background: "white",
                color: "#111",
                fontWeight: 600,
                fontSize: 14,
                borderRadius: 10,
                border: "1.5px solid #E5E5E5",
                cursor: "pointer",
              }}
            >
              Retour
            </button>
            <button
              onClick={handleSign}
              disabled={!hasSigned || signingSubmitting}
              style={{
                flex: 1,
                padding: 15,
                background: hasSigned && !signingSubmitting ? "#111" : "#94a3b8",
                color: "white",
                fontWeight: 700,
                fontSize: 14,
                borderRadius: 10,
                border: "none",
                cursor: hasSigned && !signingSubmitting ? "pointer" : "not-allowed",
                opacity: signingSubmitting ? 0.7 : 1,
              }}
            >
              {signingSubmitting ? "Envoi..." : "Valider la signature"}
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (!data) return null;

  return (
    <div className="min-h-screen bg-[#FAFAFA] flex flex-col items-center">
      <div className="w-full max-w-[600px] bg-white min-h-screen flex flex-col">
        {/* Header */}
        <div style={{ padding: "20px 20px 16px", borderBottom: "1px solid #E5E5E5" }}>
          <div style={{ fontSize: 11, fontWeight: 500, color: "#A1A1A1" }}>
            {data.org_name} {data.ordre_ref ? `\u2022 ${data.ordre_ref}` : ""}
          </div>
          <div style={{ fontSize: 18, fontWeight: 700, marginTop: 4 }}>Votre devis</div>
          <div style={{ fontSize: 12, color: "#6B7280", marginTop: 4 }}>
            Sélectionnez les interventions souhaitées puis validez.
          </div>
        </div>

        {/* Items */}
        <div style={{ padding: 16, flex: 1 }}>
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            {data.items.map((item) => {
              const selected = selectedIds.has(item.id);
              const locked = item.obligatoire;
              const expanded = expandedIds.has(item.id);

              return (
                <div
                  key={item.id}
                  style={{
                    border: `1.5px solid ${selected ? "#5C6BFF" : "#E5E5E5"}`,
                    borderRadius: 10,
                    overflow: "hidden",
                    opacity: selected ? 1 : 0.5,
                    transition: "all 0.15s",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 12,
                      padding: "12px 14px",
                      cursor: locked ? "default" : "pointer",
                      background: selected ? "#FAFBFF" : "#FAFAFA",
                    }}
                    onClick={() => toggleItem(item)}
                  >
                    {/* Checkbox */}
                    <div
                      style={{
                        width: 20,
                        height: 20,
                        borderRadius: 5,
                        border: `2px solid ${locked ? "#D1D5DB" : selected ? "#5C6BFF" : "#D1D5DB"}`,
                        background: selected ? (locked ? "#D1D5DB" : "#5C6BFF") : "white",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        flexShrink: 0,
                      }}
                    >
                      {selected && (
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="white">
                          <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41L9 16.17z" />
                        </svg>
                      )}
                    </div>

                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 13, fontWeight: 600, lineHeight: 1.3 }}>{item.libelle}</div>
                      {locked && (
                        <span style={{ fontSize: 10, color: "#9CA3AF", fontWeight: 500 }}>Inclus obligatoirement</span>
                      )}
                    </div>

                    <div style={{ textAlign: "right", flexShrink: 0 }}>
                      <div style={{ fontSize: 14, fontWeight: 700 }}>{formatPrice(getItemNet(item))}</div>
                      {item.remise != null && item.remise > 0 && (
                        <div style={{ fontSize: 10, color: "#EF4444" }}>-{item.remise}%</div>
                      )}
                    </div>

                    <div style={{ width: 32, flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "center" }}>
                      {item.sub_items.length > 0 && (
                        <button
                          style={{ padding: 10, margin: -10, color: "#9CA3AF", borderRadius: 6 }}
                          onClick={(e) => { e.stopPropagation(); toggleExpand(item.id); }}
                        >
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" style={{ transform: expanded ? "rotate(180deg)" : undefined, transition: "0.15s" }}>
                            <path d="M7 10l5 5 5-5H7z" />
                          </svg>
                        </button>
                      )}
                    </div>
                  </div>

                  {expanded && item.sub_items.length > 0 && (
                    <div style={{ borderTop: "1px solid #F0F0F0", padding: "4px 0" }}>
                      {item.sub_items.map((sub, i) => (
                        <div
                          key={i}
                          style={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            padding: "4px 14px 4px 48px",
                            fontSize: 11,
                            color: "#6B7280",
                          }}
                        >
                          <span>
                            <span style={{
                              display: "inline-block",
                              fontSize: 9,
                              fontWeight: 600,
                              padding: "1px 4px",
                              borderRadius: 3,
                              marginRight: 6,
                              background: sub.type === "piece" ? "#EEF2FF" : "#FEF3C7",
                              color: sub.type === "piece" ? "#4338CA" : "#92400E",
                            }}>
                              {sub.type === "piece" ? "PR" : "MO"}
                            </span>
                            {sub.libelle}
                          </span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* Bottom bar */}
        <div style={{
          position: "sticky",
          bottom: 0,
          background: "white",
          borderTop: "1px solid #E5E5E5",
          padding: 16,
        }}>
          <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 12, fontSize: 12 }}>
            <span style={{ color: "#6B7280" }}>Total HT</span>
            <span style={{ fontWeight: 600 }}>{formatPrice(totalHT)}</span>
          </div>
          <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 12, fontSize: 12 }}>
            <span style={{ color: "#6B7280" }}>TVA ({data.tva_rate || 20}%)</span>
            <span style={{ fontWeight: 600 }}>{formatPrice(totalTVA)}</span>
          </div>
          <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 16, fontSize: 15 }}>
            <span style={{ fontWeight: 700 }}>Total TTC</span>
            <span style={{ fontWeight: 700, color: "#5C6BFF" }}>{formatPrice(totalTTC)}</span>
          </div>
          <button
            onClick={handleSubmit}
            disabled={submitting}
            style={{
              width: "100%",
              padding: 15,
              background: "#111",
              color: "white",
              fontWeight: 700,
              fontSize: 15,
              borderRadius: 10,
              border: "none",
              cursor: submitting ? "wait" : "pointer",
              opacity: submitting ? 0.7 : 1,
            }}
          >
            {submitting ? "Validation..." : "Valider ma sélection"}
          </button>
        </div>
      </div>
    </div>
  );
}
